/**
 * One requirement on the board (prototype `vKanban`'s `.kcard`).
 *
 * Clicking opens the flow at the step where the work continues — a card in "RFQ Sent" goes
 * to Supplier Quotations, not back to triage.
 *
 * The Won/Rejected buttons are the board's ONLY mutation, and only on a sent quotation. They
 * stop the click from bubbling so deciding doesn't also navigate away.
 */

import { Badge } from '../../components/Badge'
import { Button } from '../../components/ui'
import { Money } from '../../components/Money'
import { awaitingDays, enquiryTypeTone, formatAge, formatDate, initials } from '../../lib/format'
import { canDecideRequirement } from '../../lib/kanban'
import { canSyncRequirement } from '../../lib/wonDecision'
import { replyChipLabel, replyChipTone, replyChipTooltip, type EnquiryReplyState } from '../../lib/boardReplies'
import type { EnquiryStatus, EnquirySummary } from '../../lib/types'

export function RequirementCard({
  requirement,
  effectiveStatus,
  canDecide,
  deciding,
  decideDisabled,
  replies,
  onOpen,
  onDecide,
  onDuplicate,
  onSync,
  syncing,
  duplicating,
}: {
  requirement: EnquirySummary
  effectiveStatus: EnquiryStatus
  /** Whether the acting role may decide at all (UX only — the server enforces it). */
  canDecide: boolean
  deciding: boolean
  /** Another card's decision is in flight; don't let a second one race it. */
  decideDisabled: boolean
  /**
   * How many suppliers have come back, or `null` when we do not know — still loading, the query
   * failed, or this column isn't watched.
   *
   * `null` renders NOTHING. It must never fall back to `0/N`: a card that says nobody has answered
   * because the query 403'd would send a purchaser chasing suppliers who already replied, which is
   * the failure this whole signal exists to prevent.
   */
  replies?: EnquiryReplyState | null
  onOpen: () => void
  onDecide: (to: 'Won' | 'Rejected') => void
  /** Copy this requirement — lines and priced quotation — for another order (Akhil, 2026-09-16). */
  onDuplicate?: () => void
  /** Re-send a won chain to the ERP. Shown only on a Won — see `lib/wonDecision.ts`. */
  onSync?: () => void
  syncing?: boolean
  duplicating?: boolean
}) {
  const decidable = canDecide && canDecideRequirement(requirement, effectiveStatus)
  const awaiting = awaitingDays(requirement.age_days)

  return (
    // Not role="button": the card contains real buttons, and nesting interactive content
    // inside a button role is invalid and confuses screen readers. The card stays clickable
    // for the mouse; the keyboard path is the "open flow" button in the footer.
    <div className="kcard" onClick={onOpen}>
      <div className="id">
        {requirement.id} · <Badge tone={enquiryTypeTone(requirement.enquiry_type)}>{requirement.enquiry_type}</Badge>
      </div>

      {/* Display only — `customer_name`, never the identity `customer` field. */}
      <div className="cust">{requirement.customer_name}</div>

      <div className="meta">
        <span>
          {/* The prototype shows "—" rather than a zero: a new enquiry has no value yet. */}
          {requirement.value.amount ? <Money value={requirement.value} /> : '—'}
        </span>
        {/*
          Age from the enquiry's own DATE (`transaction_date`), not `created_at`.

          This read `formatAge(requirement.created_at)` until 2026-08-09, and `get_enquiries` sends
          no creation timestamp — so it was `formatAge('')`, whose `NaN` guard returns 0, printing
          "today old" on every card on the board however old the enquiry was. A wrong age is worse
          than none: it is the field a purchaser scans to find what has been sitting.
        */}
        <span title={requirement.date ? `Raised ${formatDate(requirement.date)}` : undefined}>
          {requirement.date ? `${formatAge(requirement.date)} old` : 'no date'}
        </span>
      </div>

      {/*
        How long it has sat in THIS column, which is a different question from how old it is —
        `age_days` counts from the last status change. Same badge, same thresholds as `ContextBar`,
        so a card and the screen it opens never disagree. Hidden at 0: "moved today" is the normal
        case and a chip on every card is a chip nobody reads.
      */}
      {awaiting >= 1 ? (
        <div style={{ marginTop: 6 }}>
          <Badge tone={awaiting > 2 ? 'warn' : 'muted'} title={`No status change for ${awaiting} day(s)`}>
            Awaiting {awaiting}d
          </Badge>
        </div>
      ) : null}

      {/* Grey while the wait is ordinary, amber once suppliers have been silent past three days,
          green when everyone is in. The tooltip carries who, and whether their reply came as a
          quotation or only as mail. `total === 0` means no RFQ has actually gone out — no ratio
          to show, so no chip. */}
      {replies && replies.total > 0 ? (
        <div style={{ marginTop: 6 }}>
          <Badge tone={replyChipTone(replies)} title={replyChipTooltip(replies)}>
            {replyChipLabel(replies)}
          </Badge>
        </div>
      ) : null}

      {requirement.blocker ? (
        <div style={{ marginTop: 6 }}>
          <Badge tone="danger">⚠ {requirement.blocker}</Badge>
        </div>
      ) : null}

      {effectiveStatus === 'Archived' && requirement.status !== 'Archived' ? (
        <div style={{ marginTop: 6 }}>
          <Badge tone="muted">📦 auto-archived (&gt;30d)</Badge>
        </div>
      ) : null}

      <div className="foot">
        <span className="who-chip" title={`Purchaser: ${requirement.purchaser}`}>
          {initials(requirement.purchaser)}
        </span>
        {/* Only a requirement with a quotation has something priced to copy. */}
        {onDuplicate && requirement.quotation_id ? (
          <button
            type="button"
            className="linkish"
            style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', marginRight: 8 }}
            disabled={duplicating}
            title={`Copy every line and the priced quotation ${requirement.quotation_id} into a new requirement`}
            onClick={(e) => {
              e.stopPropagation()
              onDuplicate()
            }}
          >
            {duplicating ? 'copying…' : '⧉ duplicate'}
          </button>
        ) : null}
        {/* A Won whose push failed has no other way back to the ERP — `lib/wonDecision.ts`. */}
        {onSync && canSyncRequirement(requirement) ? (
          <button
            type="button"
            className="linkish"
            style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', marginRight: 8 }}
            disabled={syncing}
            title="Send the enquiry, supplier quotations, comparison and quotation to the ERP again"
            onClick={(e) => {
              e.stopPropagation()
              onSync()
            }}
          >
            {syncing ? 'syncing…' : '⟳ sync'}
          </button>
        ) : null}
        <button
          type="button"
          className="open linkish"
          style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation()
            onOpen()
          }}
        >
          open flow →
        </button>
      </div>

      {decidable ? (
        <div className="kdecide">
          <Button
            size="sm"
            variant="primary"
            disabled={deciding || decideDisabled}
            onClick={(e) => {
              e.stopPropagation()
              onDecide('Won')
            }}
          >
            ✓ Won
          </Button>
          <Button
            size="sm"
            variant="danger"
            disabled={deciding || decideDisabled}
            onClick={(e) => {
              e.stopPropagation()
              onDecide('Rejected')
            }}
          >
            ✗ Rejected
          </Button>
        </div>
      ) : null}
    </div>
  )
}
